// Wizard state for the meterreading flow. Holds the user-input form values
// (Home view), the matched device data after a successful ConstructSet
// lookup, and per-meter reading entries.
//
// A single MALO can resolve to multiple Zählwerke (registers) — the
// `readings` array carries one entry per matched construct, indexed
// positionally. The HomeView calls ensureReadings() after each lookup so the
// array length always matches `constructs.length`.

import { defineStore } from "pinia";
import type { ConstructEntity, AblhinweisEntity } from "@shared/odata/types";

/**
 * MRTYPE values supported by the energis meter-reading flow.
 *
 *   Common        public form: Benutzerkonto + PIN  (the standard case;
 *                 customer received a printed Ableseschreiben with both)
 *   CommonIntern  internal/staff form: Benutzerkonto + Postleitzahl
 *
 * Legacy types (`Cycle`, `SupChange`, `EEG`, `CycleXInter`, `EegXIntern`)
 * lived in the multi-tenant Web project; they're no longer triggered by
 * any energis CMS path and have been removed.
 */
export type MrType = "Common" | "CommonIntern";

export interface CredentialInput {
  serialNo: string;
  malo: string;
  pinNo: string;
  postalCode: string;
}

export interface ReadingInput {
  /** Eingegebener Zählerstand als String (kann Komma enthalten). */
  zwstandNeu: string;
  /** ADAT (raw string from toMrData[].ADAT) selected by the user via dropdown.
   *  Drives which plausi-window applies on the server side. */
  adat: string;
  /** UUID generated when an image is selected; sent as GUID_BILD with the
   *  reading after successful upload to DocumentSet. Empty until uploaded. */
  guidBild: string;
  /** Display-only filename of the uploaded image (or "" if none). */
  imageName: string;
  /** Optional: gewählter Ablesehinweis-Schlüssel, falls Plausi nötig. */
  ablhinweisKey: string;
  /** Bei ablhinweisKey === "SONS" gefüllt. */
  ablhinweisSons: string;
  /** OCR-Roh-Wert (kommaformatiert) vom letzten erfolgreichen Scan. Leer
   *  in den States "initial" und "manual". Dient nur dem Diagnose-/Audit-
   *  Pfad — der Submit liest `zwstandNeu`. */
  ocrValue: string;
  /** Anyline-Top-Level-Confidence als String "0".."100" (oder "-1" wenn der
   *  SDK keine Konfidenz liefern konnte). Wandert ungesehen ins Save-
   *  Payload-Feld OCR_CONF. */
  ocrConfidence: string;
  /** Scan-State-Machine pro Zähler:
   *    "initial"        – noch nichts erfasst (oder nur ein Bild-Upload
   *                       ohne OCR-Treffer). Input editierbar, Scan-Button
   *                       "Zählerstand scannen", Bild-Dropzone offen.
   *    "scanned"        – Input readonly, Wert+Bild von Anyline (Live-Scan
   *                       ODER Cloud-OCR mit Confidence ≥ 99); Scan-Button
   *                       "Erneut scannen" so lange `anylineScansUsed`
   *                       < `MAX_ANYLINE_SCANS` (nur Live-Scan).
   *    "ocr-suggested"  – Cloud-OCR mit Confidence < 99: Wert vorbelegt,
   *                       Input **editierbar**. Beim Submit prüft die
   *                       Logik, ob der finale Wert == ursprünglicher
   *                       OCR-Wert ist (`zwstandNeu === ocrValue`) und
   *                       setzt OCR/OCR_VALUE entsprechend.
   *
   *  Seit dem Wizard-Redesign ist "initial" zugleich der Endzustand der
   *  rein manuellen Ablesung ohne OCR-Beteiligung (Anyline-Ausfall,
   *  Kamera-Deny, aufgebrauchte Budgets) — ein Beleg-Bild kann, muss
   *  dann aber nicht vorhanden sein. */
  anylineState: "initial" | "scanned" | "ocr-suggested";
  /** Zähler verbrauchter Live-Scans. Initial 0 (noch nichts gescannt),
   *  inkrementiert pro erfolgreichem `scanMeterValue`-Aufruf. Sobald
   *  der Counter `MAX_ANYLINE_SCANS` (definiert in MeterReadingView)
   *  erreicht, verschwindet der "Erneut scannen"-Button — Live-Scans
   *  kosten pro Aufruf und sind deshalb hart gedeckelt. */
  anylineScansUsed: number;
  /** Woher der OCR-Wert kam, sofern einer existiert. `"live"` = Anyline
   *  Web-SDK-Kamerascan; `"upload"` = Anyline Cloud-OCR auf einem vom
   *  Nutzer hochgeladenen Standbild; `undefined` = kein OCR involviert.
   *  Steuert NUR die UI-Beschriftung (z. B. "Erfolgreich gescannt!" vs.
   *  Dateiname in der Drop-Zone); die Sperr-/Submit-Logik richtet sich
   *  weiterhin nach `anylineState`. */
  ocrSource?: "live" | "upload";
  /** True sobald der Nutzer diesen Zählerstand im iterativen Erfassungs-
   *  prozess bestätigt hat (Step 3a "Bestätigen", Step 3b "Übernehmen"
   *  oder manuelle Eingabe "Übernehmen"). Steuert das Checkmark/Dimmen
   *  in der Zählerübersicht (Step 1) und entscheidet, welche Readings
   *  beim Absenden mitgeschickt werden — nicht erfasste Zähler bleiben
   *  draußen und sind nach dem Absenden nicht mehr erfassbar (der
   *  Wizard warnt vor einer Teil-Übermittlung). */
  completed: boolean;
  /** Beleg-Foto eines Live-Scans, dessen Upload (noch) nicht gelang —
   *  typisch: Scan im Offline-Modus. Wird in onSubmit (läuft nur
   *  online) vor dem Absenden hochgeladen und dann samt GUID_BILD
   *  eingetragen. Bewusst NICHT serialisierbar (File) — der Store lebt
   *  ohnehin nur im Speicher der Session. */
  pendingImageFile?: File;
  /** Zuletzt aufgenommenes Beleg-Foto dieses Zählwerks in dieser Session,
   *  auch wenn der Nutzer die zugehörige Erkennung verworfen hat (Zurück,
   *  "Erneut fotografieren"). `guid` ist leer, solange der Upload nicht
   *  gelang — dann trägt `file` den Frame. Wird übernommen, wenn eine
   *  Erfassung ohne eigene Aufnahme committet (manuelle Eingabe nach
   *  aufgebrauchtem Scan-Budget): Auch ein Foto mit verworfenem
   *  Erkennungswert ist ein Beleg für energis (Fall PROD 24.10.2026, drei
   *  Fotos hochgeladen, Ablesung ohne GUID_BILD gespeichert). */
  lastPhoto?: { guid: string; name: string; file?: File };
  /** Zähler der Cloud-OCR-Fehlschläge auf hochgeladenen Bildern in
   *  dieser Session (`no-meter` und nicht-formatierbare Werte).
   *  Erreicht der Counter `UPLOAD_OCR_FAILURE_LIMIT` (= 2, in
   *  MeterReadingView), wechselt der Wizard für dieses Zählwerk auf
   *  manuelle Eingabe; das zuletzt hochgeladene Bild bleibt als Beleg
   *  in `guidBild`. Wird beim Wiedereinstieg bewusst NICHT
   *  zurückgesetzt — Cloud-OCR-Aufrufe kosten pro Stück. */
  uploadOcrFailures: number;
}

interface State {
  /** MRTYPE — drives ConstructSet $filter. */
  type: MrType;
  /** Form input on the Home view. */
  input: CredentialInput;
  /** Devices matched by the most recent ConstructSet lookup. */
  constructs: ConstructEntity[];
  /** Reading hints from AblhinweiseSet, fetched on-demand. */
  ablhinweise: AblhinweisEntity[];
  /** One reading entry per construct, kept in sync via ensureReadings(). */
  readings: ReadingInput[];
  /** Optional E-Mail (Ablesebestätigung) — applies to the whole submission. */
  email1: string;
  /** True when the DOI-Optin checkbox is ticked (whole submission). */
  optin: boolean;
  /** Snapshot used by OverviewView to display the just-saved values. */
  saved: { constructs: ConstructEntity[]; readings: ReadingInput[] } | null;
}

/**
 * Removes leading zeros from a serial number ("0001234" → "1234") while
 * preserving an alphanumeric prefix ("K0001234" stays as-is) and at least
 * one digit ("0000" → "0", "0" → "0", "" → "").
 *
 * Exported so views that display backend-supplied serial numbers (which
 * arrive zero-padded from SAP — e.g. "00000010000004587775") can format
 * them the same way the input flow normalises user-entered values.
 */
export function stripLeadingZeros(value: string): string {
  return value.replace(/^0+(?=.)/, "");
}

/**
 * Strips the leading "channel:" prefix from an OBIS-Kennziffer, so
 * "1-1:1.8.2" → "1.8.2". The channel prefix is meaningful for SAP
 * matching but noise in the customer-facing UI. Returns the input
 * unchanged if there's no colon.
 */
export function obisCode(value: string): string {
  const colon = value.indexOf(":");
  return colon >= 0 ? value.slice(colon + 1) : value;
}

function emptyReading(): ReadingInput {
  return {
    zwstandNeu: "",
    adat: "",
    guidBild: "",
    imageName: "",
    ablhinweisKey: "",
    ablhinweisSons: "",
    ocrValue: "",
    ocrConfidence: "",
    anylineState: "initial",
    anylineScansUsed: 0,
    completed: false,
    uploadOcrFailures: 0,
  };
}

export const useWizardStore = defineStore("wizard", {
  state: (): State => ({
    type: "Common",
    input: { serialNo: "", malo: "", pinNo: "", postalCode: "" },
    constructs: [],
    ablhinweise: [],
    readings: [],
    email1: "",
    optin: false,
    saved: null,
  }),
  actions: {
    setInput(patch: Partial<CredentialInput>) {
      // Normalise serial-number leading zeros at the single point where they
      // enter the store, so URL-seeded prefills (cos_serialNo=...) and form
      // submissions both go through one rule.
      if (patch.serialNo !== undefined) {
        patch = { ...patch, serialNo: stripLeadingZeros(patch.serialNo) };
      }
      Object.assign(this.input, patch);
    },
    setType(type: string | undefined) {
      const known: MrType[] = ["Common", "CommonIntern"];
      this.type = (known.find(
        (k) => k.toLowerCase() === (type ?? "").toLowerCase(),
      ) ?? "Common") as MrType;
    },
    /** Ersetzt die Constructs-Liste nach einem (neuen) Lookup und setzt
     *  Readings + Save-Snapshot zurück. Readings sind positionsgebunden —
     *  würden sie einen neuen Lookup überleben (Back zur Home-View,
     *  Login mit ANDEREM Benutzerkonto), erschienen fremde Zählerstände
     *  samt Foto-GUID als "erfasst" unter der neuen Identität und gingen
     *  beim Absenden mit. Der Route-Leave-Guard kündigt den Verlust
     *  nicht abgesendeter Eingaben ohnehin an. */
    setConstructs(constructs: ConstructEntity[]) {
      this.constructs = constructs;
      this.readings = [];
      this.saved = null;
      // Neue Anmeldung = neuer Kunde: E-Mail und Erinnerungs-Opt-in des
      // vorigen Kunden dürfen nicht in dessen Abschluss-Schritt landen
      // (Callcenter: Browser-Zurück von der Bestätigung zur Anmeldung
      // umging bis 2026-10-14 das reset() von "Weiteren Zähler ablesen").
      this.email1 = "";
      this.optin = false;
      this.ensureReadings(constructs.length);
    },
    /** Resize the `readings` array to match the number of matched constructs.
     *  Pre-fills `adat` from the first toMrData entry of each construct so the
     *  dropdown isn't empty on first render. */
    ensureReadings(count: number) {
      while (this.readings.length < count) this.readings.push(emptyReading());
      if (this.readings.length > count) this.readings.length = count;
      // Auto-select the first available ADAT for any reading that doesn't
      // have one yet.
      for (let i = 0; i < count; i++) {
        if (!this.readings[i].adat) {
          const first = this.constructs[i]?.toMrData?.results?.[0]?.ADAT;
          if (typeof first === "string") this.readings[i].adat = first;
        }
      }
    },
    setReading(idx: number, patch: Partial<ReadingInput>) {
      if (!this.readings[idx]) this.readings[idx] = emptyReading();
      Object.assign(this.readings[idx], patch);
    },
    setEmail1(value: string) {
      this.email1 = value;
    },
    setOptin(value: boolean) {
      this.optin = value;
    },
    captureSaved() {
      this.saved = {
        constructs: JSON.parse(JSON.stringify(this.constructs)),
        readings: JSON.parse(JSON.stringify(this.readings)),
      };
    },
    reset() {
      this.input = { serialNo: "", malo: "", pinNo: "", postalCode: "" };
      this.constructs = [];
      this.ablhinweise = [];
      this.readings = [];
      this.email1 = "";
      this.optin = false;
      this.saved = null;
    },
  },
});
